import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import axios from 'axios';
import { Calendar, Clock, ChevronLeft, ChevronRight, Check, AlertCircle, User, Stethoscope, CalendarDays, Building2, ArrowRight, ArrowLeft, ShoppingCart, Plus, Minus, Trash2, Car, MapPin, Loader2, CheckCircle, XCircle, Users, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Motivi della visita
const REASON_OPTIONS = [
  'Visita Oculistica Generale',
  'Controllo Vista',
  'Prescrizione Occhiali/Lenti',
  'Controllo Pressione Oculare',
  'Visita per Glaucoma',
  'Visita per Cataratta',
  'Visita per Maculopatia',
  'Visita per Distacco Retinico',
  'Visita per Occhio Secco',
  'Visita Ortottica',
  'Controllo Post-Operatorio',
  'Esami Strumentali (OCT, Campo Visivo, etc.)',
  'Urgenza Oculistica',
  'Altro'
];

// Listino Prestazioni - Aggiornato Gennaio 2026
const LISTINO_PRESTAZIONI = [
  // Visite Principali
  { id: 'visita_completa', name: 'Visita Oculistica Completa', price: 150, duration: 45, category: 'visite_principali', 
    description: 'Include: Motilità, Controllo vista, Lampada fessura, Tonometria, Esame obiettivo, Retinografia, OCT, Ecografia',
    includes: ['motilita', 'controllo_vista', 'lampada_fessura', 'tonometria', 'esame_obiettivo', 'retinografia', 'oct_macula_nervo', 'ecografia'] },
  { id: 'controllo_glaucoma', name: 'Visita di Controllo Glaucoma', price: 100, duration: 30, category: 'visite_principali',
    description: 'Include: Tonometria, Retinografia, Lampada fessura, OCT nervo ottico',
    includes: ['tonometria', 'retinografia', 'lampada_fessura', 'oct_nervo'] },
  { id: 'controllo_maculopatia', name: 'Visita di Controllo Maculopatia', price: 100, duration: 30, category: 'visite_principali',
    description: 'Include: Misurazione visus, Retinografia, Lampada fessura',
    includes: ['misurazione_visus', 'retinografia', 'lampada_fessura'] },
  
  // Prestazioni Singole Medico
  { id: 'motilita', name: 'Motilità oculare', price: 90, duration: 15, category: 'singole_medico' },
  { id: 'controllo_vista', name: 'Controllo vista con prescrizione lenti', price: 80, duration: 20, category: 'singole_medico', requiresUpload: true, uploadLabel: 'Carica vecchia prescrizione (opzionale)' },
  { id: 'lampada_fessura', name: 'Esame obiettivo alla lampada a fessura', price: 80, duration: 15, category: 'singole_medico' },
  { id: 'tonometria', name: 'Tonometria', price: 50, duration: 10, category: 'singole_medico' },
  { id: 'esame_obiettivo', name: 'Esame obiettivo', price: 100, duration: 20, category: 'singole_medico' },
  { id: 'retinografia', name: 'Retinografia', price: 80, duration: 15, category: 'singole_medico' },
  { id: 'oct_macula_nervo', name: 'OCT macula e nervo ottico', price: 100, duration: 15, category: 'singole_medico' },
  { id: 'ecografia', name: 'Ecografia screening patologie retiniche e neoplastiche', price: 120, duration: 20, category: 'singole_medico' },
  { id: 'topografia_corneale', name: 'Topografia Corneale', price: 80, duration: 20, category: 'singole_medico', note: '⚠️ No lenti a contatto 48-72h prima' },
  
  // Prestazioni Ortottista
  { id: 'visita_ortottica', name: 'Visita Ortottica Completa', price: 100, duration: 45, category: 'ortottista', 
    description: 'Include: Esame Ortottico, Misurazione visus',
    includes: ['esame_ortottico', 'misurazione_visus_ortottista'] },
  { id: 'misurazione_visus', name: 'Misurazione del visus', price: 80, duration: 15, category: 'ortottista' },
  { id: 'campo_visivo', name: 'Campo visivo computerizzato', price: 80, duration: 20, category: 'ortottista', note: 'Solo ortottista' },
  
  // Trattamenti Terapeutici
  { id: 'lllt_mgd', name: 'LLLT per MGD (Disfunzione Ghiandole di Meibomio)', price: 120, duration: 30, category: 'trattamenti' },
  { id: 'iontoretina', name: 'Iontoretina per Maculopatia', price: 250, duration: 45, category: 'trattamenti' },
  
  // Valutazioni Pre-Chirurgiche (incluse nel prezzo intervento)
  { id: 'valutazione_refrattiva', name: 'Valutazione per Chirurgia Refrattiva', price: 0, duration: 30, category: 'valutazioni', note: 'Incluso nel prezzo intervento' },
  { id: 'valutazione_cataratta', name: 'Valutazione per Chirurgia Cataratta', price: 0, duration: 30, category: 'valutazioni', note: 'Incluso nel prezzo intervento' },
  { id: 'valutazione_strabismo', name: 'Valutazione per Chirurgia Strabismo', price: 0, duration: 30, category: 'valutazioni', note: 'Incluso nel prezzo intervento' },
];

export const Booking = () => {
  // Booking step: 1 = doctor, 2 = date/time, 3 = services, 4 = confirm
  const [bookingStep, setBookingStep] = useState(1);
  
  // Booking mode: 'choose' = initial, 'by-doctor' = select doctor first, 'by-date' = select date first
  const [bookingMode, setBookingMode] = useState(null); // null | 'choose' | 'doctor' | 'date'
  
  // Location state - must be selected before anything else
  const [selectedLocation, setSelectedLocation] = useState(null); // 'milano' | 'napoli'
  
  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [userSubscription, setUserSubscription] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  
  // Doctor selection mode state
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorAvailability, setDoctorAvailability] = useState({});
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState([]);
  
  // Slot selection state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsByDoctor, setSlotsByDoctor] = useState({ morning: [], afternoon: [] });
  const [doctorsForSlot, setDoctorsForSlot] = useState([]);
  const [slotsMessage, setSlotsMessage] = useState('');
  
  // Cart state
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState({ original: 0, discounted: 0, savings: 0 });
  
  // Multi-member booking state
  // Struttura: { memberId: { date, time, doctorId, doctorName } }
  const [memberBookings, setMemberBookings] = useState({});
  const [currentEditingMember, setCurrentEditingMember] = useState(null);
  
  // WHO IS BOOKING - Step iniziale
  const [bookingFor, setBookingFor] = useState(null); // 'self' | 'family'
  const [newFamilyMember, setNewFamilyMember] = useState({
    first_name: '',
    last_name: '',
    fiscal_code: '',
    birth_date: '',
    relationship: '',
    phone: '',
    email: ''
  });
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
  const [showAddFamilyForm, setShowAddFamilyForm] = useState(false);
  
  // Driver service state (Premium only)
  const [wantDriver, setWantDriver] = useState(false);
  const [driverDetails, setDriverDetails] = useState({
    pickupTime: '',
    pickupAddress: '',
    returnAddress: ''
  });
  
  // Form state
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('Visita Oculistica'); // Default reason - auto set
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [next14DaysSlots, setNext14DaysSlots] = useState([]);
  
  // Family members state - per prenotazioni multiple
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]); // IDs dei membri selezionati
  const [loadingFamily, setLoadingFamily] = useState(false);
  
  const navigate = useNavigate();

  // Load doctors on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    loadDoctors();
    loadCurrentUser();
  }, []);
  
  // Reload doctors and calendar when location changes
  useEffect(() => {
    if (selectedLocation) {
      loadDoctors();
      loadCalendar();
    }
  }, [selectedLocation]);

  // Load current user and subscription
  const loadCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingUser(false);
      // Utente non loggato: salta direttamente alla selezione sede
      setBookingFor('self');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Blocca staff (medici, ortottisti, admin) dalla prenotazione
      const staffRoles = ['admin', 'doctor', 'ortottista', 'staff', 'receptionist'];
      if (staffRoles.includes(response.data.role)) {
        toast.error('Lo staff non può prenotare visite. Accedi come paziente.');
        navigate('/dashboard/' + response.data.role);
        return;
      }
      
      setCurrentUser(response.data);
      setUserSubscription(response.data.subscription_type || null);
      
      // Carica membri famiglia
      loadFamilyMembers(response.data.id);
    } catch (error) {
      console.error('Error loading user:', error);
      // If unauthorized, clear token
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
      }
    } finally {
      setLoadingUser(false);
    }
  };
  
  // Load family members
  const loadFamilyMembers = async (userId) => {
    setLoadingFamily(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/family-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.members) {
        setFamilyMembers(response.data.members);
      }
    } catch (error) {
      console.error('Error loading family members:', error);
    } finally {
      setLoadingFamily(false);
    }
  };
  
  // Add new family member
  const addFamilyMember = async () => {
    if (!newFamilyMember.first_name || !newFamilyMember.last_name || !newFamilyMember.birth_date || !newFamilyMember.relationship) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/family-members`, newFamilyMember, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Familiare aggiunto con successo!');
        // Ricarica i membri della famiglia
        await loadFamilyMembers(currentUser?.id);
        // Seleziona il nuovo membro
        setSelectedFamilyMember(response.data.member);
        setShowAddFamilyForm(false);
        setNewFamilyMember({
          first_name: '',
          last_name: '',
          fiscal_code: '',
          birth_date: '',
          relationship: '',
          phone: '',
          email: ''
        });
        // Passa allo step successivo
        setBookingMode('choose');
      }
    } catch (error) {
      console.error('Error adding family member:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del familiare');
    }
  };
  
  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Calculate cart total based on subscription
  useEffect(() => {
    let original = 0;
    let discounted = 0;
    
    cart.forEach(item => {
      original += item.price;
      
      // Check if included in subscription
      if (userSubscription === 'premium') {
        if (item.includedPremium === true) {
          // Fully included
          discounted += 0;
        } else if (item.includedPremium === '3/anno') {
          // IVT - 3 per year included
          discounted += 0; // Assume included for simplicity
        } else {
          discounted += item.price;
        }
      } else if (userSubscription === 'base') {
        if (item.includedBase === true) {
          discounted += 0;
        } else {
          // 20% discount on diagnostics for base
          if (item.category === 'diagnostica') {
            discounted += item.price * 0.8;
          } else {
            discounted += item.price;
          }
        }
      } else {
        discounted += item.price;
      }
    });
    
    setCartTotal({
      original: original,
      discounted: Math.round(discounted * 100) / 100,
      savings: Math.round((original - discounted) * 100) / 100
    });
  }, [cart, userSubscription]);

  // Add item to cart
  const addToCart = (service) => {
    if (cart.find(item => item.id === service.id)) {
      toast.info('Prestazione già nel carrello');
      return;
    }
    
    // Se è una visita che include altre prestazioni, mostra un messaggio
    if (service.includes && service.includes.length > 0) {
      const includedNames = service.includes
        .map(id => LISTINO_PRESTAZIONI.find(s => s.id === id)?.name)
        .filter(Boolean)
        .join(', ');
      
      toast.success(
        <div>
          <strong>{service.name}</strong> aggiunta!
          <br/>
          <span className="text-sm opacity-80">Include: {includedNames}</span>
        </div>,
        { duration: 4000 }
      );
    } else {
      toast.success(`${service.name} aggiunta al carrello`);
    }
    
    setCart([...cart, service]);
  };

  // Remove item from cart
  const removeFromCart = (serviceId) => {
    const service = cart.find(item => item.id === serviceId);
    setCart(cart.filter(item => item.id !== serviceId));
    if (service) {
      toast.info(`${service.name} rimossa dal carrello`);
    }
  };

  // Check if service is compatible with selected doctor
  const isServiceCompatible = (service) => {
    if (!selectedDoctor) return true;
    // Le prestazioni della categoria 'ortottista' sono disponibili solo se il medico selezionato è un ortottista
    if (service.category === 'ortottista' && selectedDoctor.role !== 'ortottista') return false;
    return true;
  };

  // Load calendar when month changes
  useEffect(() => {
    loadCalendar();
  }, [currentMonth, currentYear]);

  // Load next 14 days available slots on mount
  useEffect(() => {
    loadNext14DaysSlots();
  }, []);

  // Load slots when date or doctor changes
  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedDate, selectedDoctor]);

  const loadNext14DaysSlots = async () => {
    try {
      const slots = [];
      const today = new Date();
      
      for (let i = 1; i <= 14; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        
        // Skip Sundays
        if (date.getDay() === 0) continue;
        
        const dateStr = date.toISOString().split('T')[0];
        
        try {
          const response = await axios.get(`${API}/appointments/slots?date=${dateStr}`);
          // Compat: API ritorna morning_slots/afternoon_slots (canonico); leggi anche fallback legacy
          const morningArr = response.data.morning_slots || response.data.morning || [];
          const afternoonArr = response.data.afternoon_slots || response.data.afternoon || [];
          const availableCount = morningArr.length + afternoonArr.length;
          
          if (availableCount > 0) {
            slots.push({
              date: dateStr,
              dayName: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][date.getDay()],
              dayNum: date.getDate(),
              month: MONTHS[date.getMonth()],
              availableCount
            });
          }
        } catch (e) {
          // Skip errors for individual dates
        }
      }
      
      setNext14DaysSlots(slots.slice(0, 7)); // Show max 7 available dates
    } catch (error) {
      console.error('Error loading next 14 days slots:', error);
    }
  };

  const loadDoctors = async () => {
    setLoadingDoctors(true);
    try {
      let url = `${API}/doctors`;
      if (selectedLocation) {
        url += `?location=${selectedLocation}`;
      }
      const response = await axios.get(url);
      // Filter only doctors with specialization
      const activeDoctors = (response.data.doctors || []).filter(d => d.specialization && d.is_active !== false);
      setDoctors(activeDoctors);
    } catch (error) {
      console.error('Error loading doctors:', error);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const loadCalendar = async () => {
    setLoading(true);
    try {
      let url = `${API}/appointments/calendar?month=${currentMonth}&year=${currentYear}`;
      if (selectedLocation) {
        url += `&location=${selectedLocation}`;
      }
      const response = await axios.get(url);
      setCalendarData(response.data.days);
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    try {
      let url = `${API}/appointments/slots?date=${selectedDate}`;
      if (selectedDoctor) {
        url += `&doctor_id=${selectedDoctor.id}`;
      }
      if (selectedLocation) {
        url += `&location=${selectedLocation}`;
      }
      const response = await axios.get(url);
      setAvailableSlots(response.data.available_slots || []);
      setSlotsByDoctor(response.data.slots_by_doctor || { morning: [], afternoon: [] });
      setSlotsMessage(response.data.message || '');
      
      // For date-first mode, get unique doctors
      if (!selectedDoctor) {
        setDoctorsForSlot(response.data.doctors || []);
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      setAvailableSlots([]);
      setSlotsByDoctor({ morning: [], afternoon: [] });
    }
  };

  const prevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateSelect = (day) => {
    if (!day.is_working || day.available === 0) return;
    
    const today = new Date();
    const selectedDateObj = new Date(day.date);
    if (selectedDateObj < today.setHours(0,0,0,0)) return;
    
    setSelectedDate(day.date);
    setSelectedTime('');
    
    // In date-first mode, reset doctor selection
    if (bookingMode === 'by-date') {
      setSelectedDoctor(null);
    }
  };

  const handleSlotSelect = (slot, doctor = null) => {
    const slotTime = slot.time || slot;
    const slotDoctorId = doctor?.id || slot.doctor_id || selectedDoctor?.id;
    const slotDoctorName = doctor ? `Dr. ${doctor.last_name}` : 
                           slot.doctor_name ? slot.doctor_name : 
                           selectedDoctor ? `Dr. ${selectedDoctor.last_name}` : null;
    
    setSelectedTime(slotTime);
    if (doctor) {
      setSelectedDoctor(doctor);
    } else if (slot.doctor_id) {
      const doc = doctors.find(d => d.id === slot.doctor_id);
      if (doc) setSelectedDoctor(doc);
    }
    
    // Se ci sono membri selezionati, assegna lo slot al primo membro senza slot
    if (selectedMembers.length > 0 && selectedDate) {
      const membersWithoutSlot = selectedMembers.filter(id => !memberBookings[id]);
      
      if (membersWithoutSlot.length > 0) {
        const memberToAssign = membersWithoutSlot[0];
        
        setMemberBookings(prev => ({
          ...prev,
          [memberToAssign]: {
            date: selectedDate,
            time: slotTime,
            doctorId: slotDoctorId,
            doctorName: slotDoctorName
          }
        }));
        
        // Feedback
        const memberName = memberToAssign === currentUser?.id 
          ? `${currentUser.first_name}` 
          : familyMembers.find(m => m.id === memberToAssign)?.first_name || 'Membro';
        
        toast.success(`Slot assegnato a ${memberName}: ${selectedDate} ore ${slotTime}`);
        
        // Se tutti i membri hanno uno slot, passa alla sezione prestazioni
        if (membersWithoutSlot.length === 1) {
          // Era l'ultimo membro senza slot
          toast.info('Tutti gli slot assegnati! Seleziona le prestazioni e conferma.');
        }
      }
    }
  };
  
  // Rimuovi slot assegnato a un membro
  const removeMemberSlot = (memberId) => {
    setMemberBookings(prev => {
      const updated = { ...prev };
      delete updated[memberId];
      return updated;
    });
    toast.info('Slot rimosso. Seleziona un nuovo slot dal calendario.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Effettua il login per prenotare');
      navigate('/auth');
      return;
    }

    // Verifica che almeno una persona sia selezionata
    const membersToBook = selectedMembers.length > 0 ? selectedMembers : [currentUser?.id];
    if (membersToBook.length === 0 || !membersToBook[0]) {
      toast.error('Seleziona almeno una persona per la visita');
      return;
    }
    
    if (cart.length === 0) {
      toast.error('Seleziona almeno una prestazione');
      return;
    }
    
    // Verifica che tutti i membri selezionati abbiano uno slot assegnato
    const membersWithSlot = membersToBook.filter(id => memberBookings[id]);
    if (membersWithSlot.length !== membersToBook.length) {
      const missingCount = membersToBook.length - membersWithSlot.length;
      toast.error(`Mancano ${missingCount} slot da assegnare. Seleziona data e orario per ogni persona.`);
      return;
    }

    // Auto-set reason based on first cart item
    const autoReason = cart.length > 0 ? cart[0].name : 'Visita Oculistica';

    setBookingLoading(true);
    
    try {
      // Prepare treatment plan data
      const treatmentPlan = cart.map(item => ({
        service_id: item.id,
        service_name: item.name,
        price: item.price,
        duration: item.duration,
        included_base: item.includedBase,
        included_premium: item.includedPremium
      }));
      
      // Verifica se tutti gli slot sono uguali (stesso giorno/ora) o diversi
      const uniqueSlots = [...new Set(membersToBook.map(id => {
        const b = memberBookings[id];
        return b ? `${b.date}-${b.time}-${b.doctorId || ''}` : '';
      }))];
      
      const useFlexibleBooking = uniqueSlots.length > 1 || membersToBook.length > 1;
      
      if (useFlexibleBooking) {
        // Usa il nuovo endpoint flessibile
        const bookings = membersToBook.map(memberId => ({
          member_id: memberId,
          date: memberBookings[memberId].date,
          time: memberBookings[memberId].time,
          doctor_id: memberBookings[memberId].doctorId || null
        }));
        
        const bookingData = {
          bookings: bookings,
          treatment_plan: treatmentPlan,
          notes: notes
        };
        
        const response = await axios.post(
          `${API}/appointments/book-flexible?reason=${encodeURIComponent(autoReason)}&treatment_total=${cartTotal.discounted}`,
          bookingData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            } 
          }
        );
        
        if (response.data.success) {
          const numCreated = response.data.appointments?.length || membersToBook.length;
          toast.success(`🎉 Prenotati ${numCreated} appuntamenti! Conferme inviate via SMS/Email.`);
          navigate('/dashboard/patient');
        } else {
          toast.error(response.data.message || 'Errore nella prenotazione');
        }
      } else {
        // Prenotazione singola - usa il vecchio endpoint per compatibilità
        const singleSlot = memberBookings[membersToBook[0]];
        
        const bookingData = {
          treatment_plan: treatmentPlan,
          members: membersToBook,
          notes: notes
        };
        
        let url = `${API}/appointments/book-multi?date=${singleSlot.date}&time_slot=${singleSlot.time}&reason=${encodeURIComponent(autoReason)}&treatment_total=${cartTotal.discounted}`;
        if (singleSlot.doctorId) {
          url += `&doctor_id=${singleSlot.doctorId}`;
        }
        if (selectedLocation) {
          url += `&location=${selectedLocation}`;
        }
        
        const response = await axios.post(url, bookingData, { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        });
        
        const successMsg = cartTotal.discounted > 0 
          ? `Prenotazione confermata! Da pagare al check-in: €${cartTotal.discounted}`
          : 'Prenotazione confermata! Nessun pagamento richiesto grazie al tuo abbonamento.';
        
        toast.success(successMsg);
        navigate('/dashboard/patient');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setBookingLoading(false);
    }
  };

  const resetSelection = () => {
    setSelectedDate('');
    setSelectedTime('');
    setSelectedDoctor(null);
    setAvailableSlots([]);
    setSlotsByDoctor({ morning: [], afternoon: [] });
    setBookingMode('choose');
  };
  
  const resetToLocationSelection = () => {
    resetSelection();
    setSelectedLocation(null);
    setBookingMode(null);
  };

  // Calendar helpers
  const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();
  const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const today = new Date();
  const isPastMonth = currentYear < today.getFullYear() || (currentYear === today.getFullYear() && currentMonth < today.getMonth() + 1);
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const isFutureMonth = currentYear > maxDate.getFullYear() || (currentYear === maxDate.getFullYear() && currentMonth > maxDate.getMonth() + 1);

  // Room colors
  const getRoomColor = (room) => {
    const colors = {
      'Stanza A': { bg: '#DBEAFE', text: '#2A9FCB' },
      'Stanza B': { bg: '#FEF3C7', text: '#B45309' },
      'Stanza C': { bg: '#D1FAE5', text: '#047857' },
      'Stanza D': { bg: '#EDE9FE', text: '#6D28D9' }
    };
    return colors[room] || { bg: '#F3F4F6', text: '#4B5563' };
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Header - Luxury */}
      <div className="pt-32 pb-16 px-6 md:px-12" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="max-w-5xl mx-auto text-center">
          <p 
            className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
            style={{ color: '#2A9FCB' }}
          >
            Prenotazioni
          </p>
          <h1 
            className="text-4xl md:text-5xl font-normal mb-4 tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            Prenota una Visita
          </h1>
          <p className="text-lg" style={{ color: '#666666' }}>
            Scegli come preferisci prenotare il tuo appuntamento
          </p>
        </div>
      </div>
      
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          
          {/* STEP 0: Per chi prenoti? - Solo per utenti loggati come paziente */}
          {bookingFor === null && bookingMode === null && currentUser && (
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-medium text-center mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Per chi vuoi prenotare?
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Prenota per me */}
                <button
                  onClick={() => {
                    setBookingFor('self');
                    setSelectedFamilyMember(null);
                  }}
                  className="rounded-2xl bg-white border border-gray-100 p-8 text-left transition-all duration-500 hover:border-[#2A9FCB]/30 hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] group"
                  data-testid="booking-for-self"
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-[#2A9FCB]/10 group-hover:bg-[#2A9FCB] transition-colors duration-300">
                    <User className="w-8 h-8 text-[#2A9FCB] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-medium mb-3 group-hover:text-[#2A9FCB] transition-colors" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}>
                    Prenota per Me
                  </h3>
                  <p className="mb-4" style={{ color: '#666666' }}>
                    La visita è per te stesso. Procedi con la prenotazione.
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Seleziona</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>

                {/* Prenota per un familiare */}
                <button
                  onClick={() => setBookingFor('family')}
                  className="rounded-2xl bg-white border border-gray-100 p-8 text-left transition-all duration-500 hover:border-[#2A9FCB]/30 hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] group"
                  data-testid="booking-for-family"
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-[#2A9FCB]/10 group-hover:bg-[#2A9FCB] transition-colors duration-300">
                    <Users className="w-8 h-8 text-[#2A9FCB] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-medium mb-3 group-hover:text-[#2A9FCB] transition-colors" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}>
                    Prenota per un Familiare
                  </h3>
                  <p className="mb-4" style={{ color: '#666666' }}>
                    La visita è per un componente del tuo nucleo familiare.
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Seleziona</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* STEP 0.5: Seleziona o aggiungi familiare */}
          {bookingFor === 'family' && bookingMode === null && (
            <div className="max-w-3xl mx-auto">
              <button
                onClick={() => setBookingFor(null)}
                className="flex items-center gap-2 text-gray-500 hover:text-[#2A9FCB] mb-8"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Indietro</span>
              </button>
              
              <h2 className="text-2xl font-medium text-center mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Per quale familiare vuoi prenotare?
              </h2>
              
              {/* Lista familiari esistenti + opzione Altro */}
              <div className="space-y-4 mb-8">
                {/* Familiari già registrati */}
                {familyMembers.map(member => (
                  <button
                    key={member.id}
                    onClick={() => {
                      setSelectedFamilyMember(member);
                      setShowAddFamilyForm(false);
                      setBookingMode('choose');
                    }}
                    className="w-full p-5 rounded-xl border-2 text-left transition-all hover:border-[#2A9FCB] hover:bg-[#2A9FCB]/5 border-gray-100 group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#2A9FCB]/10 flex items-center justify-center group-hover:bg-[#2A9FCB] transition-colors">
                        <User className="w-6 h-6 text-[#2A9FCB] group-hover:text-white transition-colors" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-lg">{member.first_name} {member.last_name}</p>
                        <p className="text-sm text-gray-500">{member.relationship} • {new Date(member.birth_date).toLocaleDateString('it-IT')}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#2A9FCB] transition-colors" />
                    </div>
                  </button>
                ))}
                
                {/* Opzione "Altro..." per aggiungere nuovo familiare */}
                {!showAddFamilyForm && (
                  <button
                    onClick={() => setShowAddFamilyForm(true)}
                    className="w-full p-5 rounded-xl border-2 border-dashed border-gray-200 hover:border-[#2A9FCB] text-gray-500 hover:text-[#2A9FCB] transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#2A9FCB]/10 transition-colors">
                        <Plus className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-lg">Altro...</p>
                        <p className="text-sm opacity-70">Registra un nuovo familiare</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
              
              {/* Form per nuovo familiare */}
              {showAddFamilyForm && (
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-medium text-gray-900 mb-4">Nuovo Familiare</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input
                        type="text"
                        value={newFamilyMember.first_name}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, first_name: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none"
                        placeholder="Nome"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                      <input
                        type="text"
                        value={newFamilyMember.last_name}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, last_name: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none"
                        placeholder="Cognome"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita *</label>
                      <input
                        type="date"
                        value={newFamilyMember.birth_date}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, birth_date: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parentela *</label>
                      <select
                        value={newFamilyMember.relationship}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, relationship: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none"
                      >
                        <option value="">Seleziona...</option>
                        <option value="Figlio/a">Figlio/a</option>
                        <option value="Coniuge">Coniuge</option>
                        <option value="Genitore">Genitore</option>
                        <option value="Fratello/Sorella">Fratello/Sorella</option>
                        <option value="Nonno/a">Nonno/a</option>
                        <option value="Nipote">Nipote</option>
                        <option value="Altro">Altro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                      <input
                        type="text"
                        value={newFamilyMember.fiscal_code}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, fiscal_code: e.target.value.toUpperCase()})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none uppercase"
                        placeholder="RSSMRA80A01H501Z"
                        maxLength={16}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                      <input
                        type="tel"
                        value={newFamilyMember.phone}
                        onChange={(e) => setNewFamilyMember({...newFamilyMember, phone: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-[#2A9FCB] focus:ring-1 focus:ring-[#2A9FCB] outline-none"
                        placeholder="+39 333 1234567"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => setShowAddFamilyForm(false)}
                      className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={addFamilyMember}
                      className="flex-1 py-3 rounded-xl text-white font-medium"
                      style={{ background: '#2A9FCB' }}
                    >
                      Aggiungi e Continua
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* STEP 0.75: Selezione Sede - Prima di scegliere medico o data */}
          {bookingFor !== null && selectedLocation === null && bookingMode === null && (bookingFor === 'self' || selectedFamilyMember) && (
            <div className="max-w-3xl mx-auto">
              {/* Back button */}
              {currentUser && (
                <button
                  onClick={() => {
                    if (selectedFamilyMember) {
                      setBookingFor('family');
                      setSelectedFamilyMember(null);
                    } else {
                      setBookingFor(null);
                    }
                  }}
                  className="flex items-center gap-2 text-gray-500 hover:text-[#2A9FCB] mb-8"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Indietro</span>
                </button>
              )}
              
              <h2 className="text-2xl font-medium text-center mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                In quale sede vuoi prenotare?
              </h2>
              <p className="text-center mb-10" style={{ color: '#666666' }}>
                Seleziona la clinica dove desideri effettuare la visita
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Milano */}
                <div className="rounded-2xl bg-white border-2 border-gray-100 overflow-hidden transition-all duration-500 hover:border-orange-300 hover:shadow-[0_8px_30px_rgba(249,115,22,0.15)] group relative">
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-orange-400 to-orange-500 z-10" />
                  {/* Map */}
                  <div className="w-full h-48 relative">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2798.4!2d9.196!3d45.459!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4786c6a6a0c1f3d1%3A0x0!2sVia+Santa+Sofia+18%2C+Milano!5e0!3m2!1sit!2sit!4v1"
                      className="w-full h-full border-0"
                      allowFullScreen=""
                      loading="lazy"
                      title="Milano - Via Santa Sofia 10"
                    />
                    <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow">Milano</div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedLocation('milano');
                      setBookingMode('choose');
                    }}
                    className="w-full p-6 text-left"
                    data-testid="location-milano"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center bg-orange-50 group-hover:bg-orange-500 transition-colors duration-300 flex-shrink-0">
                        <MapPin className="w-6 h-6 text-orange-500 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div className="flex-1">
                        <h3 
                          className="text-xl font-medium mb-1 group-hover:text-orange-500 transition-colors"
                          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                        >
                          Milano
                        </h3>
                        <p className="text-sm mb-3" style={{ color: '#666666' }}>
                          Via Santa Sofia 10, 20122 Milano
                        </p>
                        <div className="flex items-center gap-2 text-sm font-medium text-orange-500">
                          <span>Seleziona questa sede</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </button>
                  <a
                    href="https://www.google.com/maps/dir//Via+Santa+Sofia+18,+Milano"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-3 bg-orange-50 text-orange-600 text-sm font-medium hover:bg-orange-100 transition-colors border-t border-orange-100"
                    data-testid="directions-milano"
                  >
                    Come Raggiungerci
                  </a>
                </div>

                {/* Napoli */}
                <div className="rounded-2xl bg-white border-2 border-gray-100 overflow-hidden transition-all duration-500 hover:border-blue-300 hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] group relative">
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-400 to-blue-600 z-10" />
                  {/* Map */}
                  <div className="w-full h-48 relative">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.8!2d14.203!3d40.823!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x133b091e94e4c2f5%3A0x0!2sVia+Posillipo+160%2C+Napoli!5e0!3m2!1sit!2sit!4v1"
                      className="w-full h-full border-0"
                      allowFullScreen=""
                      loading="lazy"
                      title="Napoli - Via Posillipo 160"
                    />
                    <div className="absolute top-2 right-2 bg-[#2A9FCB] text-white text-xs px-2 py-1 rounded-full font-medium shadow">Napoli</div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedLocation('napoli');
                      setBookingMode('choose');
                    }}
                    className="w-full p-6 text-left"
                    data-testid="location-napoli"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-50 group-hover:bg-[#2A9FCB] transition-colors duration-300 flex-shrink-0">
                        <MapPin className="w-6 h-6 text-[#2A9FCB] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div className="flex-1">
                        <h3 
                          className="text-xl font-medium mb-1 group-hover:text-[#2A9FCB] transition-colors"
                          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                        >
                          Napoli
                        </h3>
                        <p className="text-sm mb-3" style={{ color: '#666666' }}>
                          Via Posillipo 160 — Clinica Posillipo
                        </p>
                        <div className="flex items-center gap-2 text-sm font-medium text-[#2A9FCB]">
                          <span>Seleziona questa sede</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </button>
                  <a
                    href="https://www.google.com/maps/dir//Via+Posillipo+160,+Napoli"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-3 bg-blue-50 text-[#2A9FCB] text-sm font-medium hover:bg-blue-100 transition-colors border-t border-blue-100"
                    data-testid="directions-napoli"
                  >
                    Come Raggiungerci
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* Mode Selection - Luxury Cards */}
          {bookingMode === 'choose' && (
            <div className="max-w-3xl mx-auto">
              {/* Banner: Per chi stai prenotando */}
              {selectedFamilyMember && (
                <div className="mb-8 p-4 rounded-xl bg-[#2A9FCB]/10 border border-[#2A9FCB]/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-[#2A9FCB]" />
                      <span className="text-gray-700">
                        Stai prenotando per: <strong className="text-[#2A9FCB]">{selectedFamilyMember.first_name} {selectedFamilyMember.last_name}</strong>
                        <span className="text-gray-500 text-sm ml-2">({selectedFamilyMember.relationship})</span>
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setBookingFor('family');
                        setBookingMode(null);
                      }}
                      className="text-sm text-[#2A9FCB] hover:underline"
                    >
                      Cambia
                    </button>
                  </div>
                </div>
              )}
              
              {/* Back button */}
              <button
                onClick={() => {
                  setSelectedLocation(null);
                  setBookingMode(null);
                }}
                className="flex items-center gap-2 text-gray-500 hover:text-[#2A9FCB] mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Indietro</span>
              </button>
              
              {/* Location badge */}
              {selectedLocation && (
                <div className={`mb-8 p-4 rounded-xl border ${selectedLocation === 'milano' ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MapPin className={`w-5 h-5 ${selectedLocation === 'milano' ? 'text-orange-500' : 'text-[#2A9FCB]'}`} />
                      <span className="text-gray-700">
                        Sede: <strong className={selectedLocation === 'milano' ? 'text-orange-600' : 'text-[#2A9FCB]'}>
                          {selectedLocation === 'milano' ? 'Milano — Via Santa Sofia 10' : 'Napoli — Via Posillipo 160'}
                        </strong>
                      </span>
                    </div>
                    <button
                      onClick={() => { setSelectedLocation(null); setBookingMode(null); }}
                      className={`text-sm hover:underline ${selectedLocation === 'milano' ? 'text-orange-500' : 'text-[#2A9FCB]'}`}
                    >
                      Cambia
                    </button>
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-8">
                {/* By Doctor */}
                <button
                  onClick={() => setBookingMode('by-doctor')}
                  className="rounded-2xl bg-white border border-gray-100 p-8 text-left transition-all duration-500 hover:border-[#2A9FCB]/30 hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] group"
                  data-testid="mode-by-doctor"
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-[#2A9FCB]/10 group-hover:bg-[#2A9FCB] transition-colors duration-300">
                    <Stethoscope className="w-8 h-8 text-[#2A9FCB] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 
                    className="text-xl font-medium mb-3 group-hover:text-[#2A9FCB] transition-colors"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                  >
                    Scegli per Medico
                  </h3>
                  <p className="mb-4" style={{ color: '#666666' }}>
                    Sai già quale specialista ti serve? Seleziona il medico e vedi quando è disponibile.
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Seleziona</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>

                {/* By Date */}
                <button
                  onClick={() => setBookingMode('by-date')}
                  className="rounded-2xl bg-white border border-gray-100 p-8 text-left transition-all duration-500 hover:border-[#2A9FCB]/30 hover:shadow-[0_8px_30px_rgba(42,159,203,0.15)] group"
                  data-testid="mode-by-date"
                >
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-[#2A9FCB]/10 group-hover:bg-[#2A9FCB] transition-colors duration-300">
                    <CalendarDays className="w-8 h-8 text-[#2A9FCB] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 
                    className="text-xl font-medium mb-3 group-hover:text-[#2A9FCB] transition-colors"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
                  >
                    Scegli per Data
                  </h3>
                  <p className="mb-4" style={{ color: '#666666' }}>
                    Preferisci un giorno specifico? Scegli la data e poi il medico disponibile.
                  </p>
                  <div className="flex items-center gap-2 text-sm font-medium text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Seleziona</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* By Doctor Mode */}
          {bookingMode === 'by-doctor' && !selectedDoctor && (
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={resetSelection}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-[#666666] hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Indietro
                </button>
                <p className="text-lg" style={{color: '#666666'}}>
                  Seleziona il medico con cui vuoi prenotare
                </p>
              </div>
              
              {/* Location indicator */}
              {selectedLocation && (
                <div className={`mb-6 px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-medium ${selectedLocation === 'milano' ? 'bg-orange-50 text-orange-600 border border-orange-200' : 'bg-blue-50 text-[#2A9FCB] border border-blue-200'}`}>
                  <MapPin className="w-4 h-4" />
                  {selectedLocation === 'milano' ? 'Milano' : 'Napoli'}
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doctors.map(doctor => {
                  const roomColor = getRoomColor(doctor.room);
                  return (
                    <button
                      key={doctor.id}
                      onClick={() => setSelectedDoctor(doctor)}
                      className="rounded-xl shadow-md p-5 text-left transition-all hover:scale-105 hover:shadow-lg bg-white"
                      data-testid={`doctor-card-${doctor.id}`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#2A9FCB'}}>
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-[#2A9FCB] truncate">
                            Dott. {doctor.first_name} {doctor.last_name}
                          </h3>
                          <p className="text-sm font-medium" style={{color: '#2A9FCB'}}>
                            {doctor.specialization}
                          </p>
                        </div>
                      </div>
                      {doctor.room && (
                        <div className="flex items-center gap-2">
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full  text-xs font-medium"
                            style={{background: roomColor.bg, color: roomColor.text}}
                          >
                            <Building2 className="w-3 h-3" />
                            {doctor.room}
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {doctors.length === 0 && !loadingDoctors && (
                <div className="text-center py-12 text-[#2A9FCB]/70">
                  <Stethoscope className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Nessun medico disponibile</p>
                </div>
              )}
            </div>
          )}

          {/* Calendar View - shown when doctor is selected (by-doctor) or in by-date mode */}
          {((bookingMode === 'by-doctor' && selectedDoctor) || bookingMode === 'by-date') && (
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => {
                    if (bookingMode === 'by-doctor') {
                      setSelectedDoctor(null);
                      setSelectedDate('');
                      setSelectedTime('');
                    } else {
                      resetSelection();
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-[#2A9FCB]/80 hover:bg-white/50"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Indietro
                </button>
                
                {selectedDoctor && (
                  <div className="flex items-center gap-3 px-4 py-2 rounded-lg" style={{background: '#2A9FCB'}}>
                    <User className="w-5 h-5 text-white" />
                    <div>
                      <p className="text-white font-medium">Dott. {selectedDoctor.first_name} {selectedDoctor.last_name}</p>
                      <p className="text-white/80 text-sm">{selectedDoctor.specialization}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Calendar */}
                <div className="rounded-2xl shadow-lg p-6" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-6">
                    <button 
                      onClick={prevMonth} 
                      disabled={isPastMonth}
                      className="p-2 rounded-full hover:bg-white/50 disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{color: '#2A9FCB'}}
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <h2 className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                      {MONTHS[currentMonth - 1]} {currentYear}
                    </h2>
                    <button 
                      onClick={nextMonth}
                      disabled={isFutureMonth}
                      className="p-2 rounded-full hover:bg-white/50 disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{color: '#2A9FCB'}}
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Weekday Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {WEEKDAYS.map(day => (
                      <div key={day} className="text-center text-sm font-medium py-2" style={{color: '#6B7280'}}>
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: offset }).map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square" />
                    ))}
                    
                    {calendarData.map(day => {
                      const isToday = day.date === today.toISOString().split('T')[0];
                      const isPast = new Date(day.date) < new Date().setHours(0,0,0,0);
                      const isSelected = day.date === selectedDate;
                      const hasAvailability = day.is_working && day.available > 0 && !isPast;
                      
                      return (
                        <button
                          key={day.date}
                          onClick={() => handleDateSelect(day)}
                          disabled={!hasAvailability}
                          className={`
                            aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition-all relative
                            ${isSelected ? 'ring-2 ring-offset-2' : ''}
                            ${hasAvailability ? 'hover:scale-105 cursor-pointer' : 'cursor-not-allowed opacity-50'}
                            ${isToday ? 'font-bold' : ''}
                          `}
                          style={{
                            background: isSelected ? '#2A9FCB' : hasAvailability ? '#fff' : '#E5E7EB',
                            color: isSelected ? '#fff' : hasAvailability ? '#2A9FCB' : '#9CA3AF',
                            '--tw-ring-color': '#2A9FCB'
                          }}
                          data-testid={`calendar-day-${day.day}`}
                        >
                          <span>{day.day}</span>
                          {day.is_working && !isPast && (
                            <span className={`text-[10px] ${isSelected ? 'text-white/80' : ''}`} style={{color: isSelected ? 'rgba(255,255,255,0.8)' : day.available > 5 ? '#10B981' : day.available > 0 ? '#F59E0B' : '#EF4444'}}>
                              {day.available} slot
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Booking Form */}
                <div className="rounded-2xl shadow-lg p-6" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                    
                    {/* Family Member Selection */}
                    {currentUser && (
                      <div className="border-b pb-4 mb-4">
                        <label className="flex items-center text-sm font-medium mb-3" style={{color: '#2A9FCB'}}>
                          <Users className="w-5 h-5 mr-2" />
                          Chi deve fare la visita?
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Per ogni persona puoi scegliere giorno, orario e medico diverso. Clicca su "Scegli Slot" per configurare.
                        </p>
                        
                        <div className="space-y-3">
                          {/* Current user (self) */}
                          <div className={`p-4 rounded-xl border-2 transition-all ${
                            selectedMembers.includes(currentUser.id) 
                              ? 'border-[#2A9FCB] bg-[#2A9FCB]/5' 
                              : 'border-gray-200 bg-white'
                          }`}>
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => toggleMemberSelection(currentUser.id)}
                                className="flex items-center gap-3 flex-1"
                                data-testid="select-self"
                              >
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  selectedMembers.includes(currentUser.id) ? 'border-[#2A9FCB] bg-[#2A9FCB]' : 'border-gray-300'
                                }`}>
                                  {selectedMembers.includes(currentUser.id) && (
                                    <Check className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div className="text-left">
                                  <p className="font-medium text-gray-900">
                                    {currentUser.first_name} {currentUser.last_name}
                                    <span className="ml-2 text-xs px-2 py-0.5 bg-[#2A9FCB]/20 text-[#2A9FCB] rounded-full">Tu</span>
                                  </p>
                                </div>
                              </button>
                              
                              {selectedMembers.includes(currentUser.id) && (
                                <div className="text-right flex items-center gap-2">
                                  {memberBookings[currentUser.id] ? (
                                    <>
                                      <div className="text-xs">
                                        <p className="text-green-600 font-medium">✓ {memberBookings[currentUser.id].date} - {memberBookings[currentUser.id].time}</p>
                                        {memberBookings[currentUser.id].doctorName && (
                                          <p className="text-gray-500">{memberBookings[currentUser.id].doctorName}</p>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeMemberSlot(currentUser.id)}
                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                        title="Rimuovi slot"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded">⚠ Seleziona slot</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Family members */}
                          {familyMembers.map(member => (
                            <div 
                              key={member.id}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                selectedMembers.includes(member.id) 
                                  ? 'border-[#2A9FCB] bg-[#2A9FCB]/5' 
                                  : 'border-gray-200 bg-white'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => toggleMemberSelection(member.id)}
                                  className="flex items-center gap-3 flex-1"
                                  data-testid={`select-member-${member.id}`}
                                >
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    selectedMembers.includes(member.id) ? 'border-[#2A9FCB] bg-[#2A9FCB]' : 'border-gray-300'
                                  }`}>
                                    {selectedMembers.includes(member.id) && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <div className="text-left">
                                    <p className="font-medium text-gray-900">
                                      {member.first_name} {member.last_name}
                                      {member.is_minor && (
                                        <span className="ml-2 text-xs px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full">Minore</span>
                                      )}
                                    </p>
                                  </div>
                                </button>
                                
                                {selectedMembers.includes(member.id) && (
                                  <div className="text-right flex items-center gap-2">
                                    {memberBookings[member.id] ? (
                                      <>
                                        <div className="text-xs">
                                          <p className="text-green-600 font-medium">✓ {memberBookings[member.id].date} - {memberBookings[member.id].time}</p>
                                          {memberBookings[member.id].doctorName && (
                                            <p className="text-gray-500">{memberBookings[member.id].doctorName}</p>
                                          )}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeMemberSlot(member.id)}
                                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                                          title="Rimuovi slot"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded">⚠ Seleziona slot</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {selectedMembers.length > 0 && (
                          <div className="mt-3 p-3 rounded-xl bg-blue-50 text-blue-700 text-sm">
                            <strong>{selectedMembers.length} persona/e selezionata/e</strong>
                            {Object.keys(memberBookings).length < selectedMembers.length && (
                              <span className="ml-2 text-orange-600">
                                - Mancano {selectedMembers.length - Object.keys(memberBookings).length} slot da assegnare
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Selected Date Display */}
                    <div className="p-4 rounded-xl" style={{background: selectedDate ? '#2A9FCB' : '#E5E7EB'}}>
                      <div className="flex items-center gap-3">
                        <Calendar className={`w-6 h-6 ${selectedDate ? 'text-white' : 'text-[#2A9FCB]/60'}`} />
                        <div>
                          <p className={`text-sm ${selectedDate ? 'text-white/80' : 'text-[#2A9FCB]/60'}`}>Data selezionata</p>
                          <p className={`font-bold ${selectedDate ? 'text-white' : 'text-[#2A9FCB]/70'}`}>
                            {selectedDate ? new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Seleziona dal calendario'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Time Slots */}
                    {selectedDate && (
                      <div>
                        <label className="flex items-center text-sm font-medium mb-3" style={{color: '#2A9FCB'}}>
                          <Clock className="w-5 h-5 mr-2" />
                          Seleziona Orario
                        </label>
                        
                        {slotsMessage && (
                          <div className="flex items-center gap-2 p-3 rounded-lg mb-3" style={{background: '#FEF3C7'}}>
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm text-amber-700">{slotsMessage}</span>
                          </div>
                        )}
                        
                        {/* By Doctor Mode - Simple time slots */}
                        {bookingMode === 'by-doctor' && selectedDoctor && availableSlots.length > 0 && (
                          <>
                            <p className="text-xs font-medium mb-2" style={{color: '#6B7280'}}>Mattina (9:30 - 12:30)</p>
                            <div className="grid grid-cols-4 gap-2 mb-4">
                              {availableSlots.filter(s => parseInt(s.split(':')[0]) < 13).map(slot => (
                                <button
                                  key={slot}
                                  type="button"
                                  onClick={() => handleSlotSelect(slot)}
                                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                    selectedTime === slot ? 'text-white' : 'bg-white hover:scale-105'
                                  }`}
                                  style={{
                                    background: selectedTime === slot ? '#2A9FCB' : '#fff',
                                    color: selectedTime === slot ? '#fff' : '#2A9FCB',
                                    border: `1px solid ${selectedTime === slot ? '#2A9FCB' : '#E5E7EB'}`
                                  }}
                                  data-testid={`time-slot-${slot}`}
                                >
                                  {slot}
                                </button>
                              ))}
                            </div>
                            
                            {availableSlots.some(s => parseInt(s.split(':')[0]) >= 15) && (
                              <>
                                <p className="text-xs font-medium mb-2" style={{color: '#6B7280'}}>Pomeriggio (15:30 - 18:30)</p>
                                <div className="grid grid-cols-4 gap-2">
                                  {availableSlots.filter(s => parseInt(s.split(':')[0]) >= 15).map(slot => (
                                    <button
                                      key={slot}
                                      type="button"
                                      onClick={() => handleSlotSelect(slot)}
                                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                        selectedTime === slot ? 'text-white' : 'bg-white hover:scale-105'
                                      }`}
                                      style={{
                                        background: selectedTime === slot ? '#2A9FCB' : '#fff',
                                        color: selectedTime === slot ? '#fff' : '#2A9FCB',
                                        border: `1px solid ${selectedTime === slot ? '#2A9FCB' : '#E5E7EB'}`
                                      }}
                                      data-testid={`time-slot-${slot}`}
                                    >
                                      {slot}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        )}

                        {/* By Date Mode - Slots with doctor info */}
                        {bookingMode === 'by-date' && (
                          <>
                            {/* Morning slots by doctor */}
                            {slotsByDoctor.morning?.length > 0 && (
                              <>
                                <p className="text-xs font-medium mb-2" style={{color: '#6B7280'}}>Mattina (9:30 - 12:30)</p>
                                <div className="space-y-2 mb-4">
                                  {slotsByDoctor.morning.map((slot, idx) => {
                                    const roomColor = getRoomColor(slot.room);
                                    const isSelected = selectedTime === slot.time && selectedDoctor?.id === slot.doctor_id;
                                    return (
                                      <button
                                        key={`${slot.time}-${slot.doctor_id}-${idx}`}
                                        type="button"
                                        onClick={() => handleSlotSelect(slot, { id: slot.doctor_id, first_name: slot.doctor_name?.split(' ')[0] || '', last_name: slot.doctor_name?.split(' ').slice(1).join(' ') || slot.doctor_name })}
                                        className={`w-full p-3 rounded-lg text-left transition-all ${isSelected ? '' : 'bg-white hover:shadow-md'}`}
                                        style={{
                                          background: isSelected ? '#2A9FCB' : '#fff',
                                          border: `2px solid ${isSelected ? '#2A9FCB' : '#E5E7EB'}`
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <span className={`text-lg font-bold ${isSelected ? 'text-white' : ''}`} style={{color: isSelected ? '#fff' : '#2A9FCB'}}>
                                              {slot.time}
                                            </span>
                                            <div>
                                              <p className={`font-medium ${isSelected ? 'text-white' : 'text-[#2A9FCB]'}`}>
                                                {slot.doctor_name || 'Medico'}
                                              </p>
                                            </div>
                                          </div>
                                          {slot.room && (
                                            <span 
                                              className="text-xs px-2 py-1 rounded-full "
                                              style={{background: isSelected ? 'rgba(255,255,255,0.2)' : roomColor.bg, color: isSelected ? '#fff' : roomColor.text}}
                                            >
                                              {slot.room}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}

                            {/* Afternoon slots by doctor */}
                            {slotsByDoctor.afternoon?.length > 0 && (
                              <>
                                <p className="text-xs font-medium mb-2" style={{color: '#6B7280'}}>Pomeriggio (15:30 - 18:30)</p>
                                <div className="space-y-2">
                                  {slotsByDoctor.afternoon.map((slot, idx) => {
                                    const roomColor = getRoomColor(slot.room);
                                    const isSelected = selectedTime === slot.time && selectedDoctor?.id === slot.doctor_id;
                                    return (
                                      <button
                                        key={`${slot.time}-${slot.doctor_id}-${idx}`}
                                        type="button"
                                        onClick={() => handleSlotSelect(slot, { id: slot.doctor_id, first_name: slot.doctor_name?.split(' ')[0] || '', last_name: slot.doctor_name?.split(' ').slice(1).join(' ') || slot.doctor_name })}
                                        className={`w-full p-3 rounded-lg text-left transition-all ${isSelected ? '' : 'bg-white hover:shadow-md'}`}
                                        style={{
                                          background: isSelected ? '#2A9FCB' : '#fff',
                                          border: `2px solid ${isSelected ? '#2A9FCB' : '#E5E7EB'}`
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <span className={`text-lg font-bold ${isSelected ? 'text-white' : ''}`} style={{color: isSelected ? '#fff' : '#2A9FCB'}}>
                                              {slot.time}
                                            </span>
                                            <div>
                                              <p className={`font-medium ${isSelected ? 'text-white' : 'text-[#2A9FCB]'}`}>
                                                {slot.doctor_name || 'Medico'}
                                              </p>
                                            </div>
                                          </div>
                                          {slot.room && (
                                            <span 
                                              className="text-xs px-2 py-1 rounded-full "
                                              style={{background: isSelected ? 'rgba(255,255,255,0.2)' : roomColor.bg, color: isSelected ? '#fff' : roomColor.text}}
                                            >
                                              {slot.room}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              </>
                            )}

                            {slotsByDoctor.morning?.length === 0 && slotsByDoctor.afternoon?.length === 0 && (
                              <p className="text-center text-[#2A9FCB]/70 py-4">Nessuno slot disponibile per questa data</p>
                            )}
                          </>
                        )}

                        {bookingMode === 'by-doctor' && availableSlots.length === 0 && (
                          <p className="text-center text-[#2A9FCB]/70 py-4">Nessuno slot disponibile per questa data</p>
                        )}
                      </div>
                    )}

                    {/* === DATE DISPONIBILI PROSSIMI 14 GIORNI === */}
                    {next14DaysSlots.length > 0 && !selectedDate && (
                      <div className="p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                        <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                          <CalendarDays className="w-5 h-5" />
                          🗓️ Prossime Date Disponibili
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {next14DaysSlots.map((slot) => (
                            <button
                              key={slot.date}
                              onClick={() => {
                                setSelectedDate(slot.date);
                                // Update calendar to show the right month
                                const d = new Date(slot.date);
                                setCurrentMonth(d.getMonth() + 1);
                                setCurrentYear(d.getFullYear());
                              }}
                              className="flex flex-col items-center px-4 py-3 rounded-xl bg-white border-2 border-green-200 hover:border-green-400 hover:shadow-md transition-all"
                            >
                              <span className="text-xs text-gray-500 uppercase">{slot.dayName}</span>
                              <span className="text-xl font-bold text-green-700">{slot.dayNum}</span>
                              <span className="text-xs text-gray-600">{slot.month.substring(0, 3)}</span>
                              <span className="mt-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                {slot.availableCount} slot
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-green-700 mt-3">
                          Clicca su una data per vedere gli orari disponibili
                        </p>
                      </div>
                    )}

                    {/* === PIANO DI TRATTAMENTO === */}
                    {selectedTime && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg" style={{color: '#2A9FCB'}}>
                            Piano di Cura
                          </h3>
                          {userSubscription && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold" 
                                  style={{
                                    background: userSubscription === 'PREMIUM' ? '#FEF3C7' : userSubscription === 'BASE' ? '#F3F4F6' : '#CCFBF1',
                                    color: userSubscription === 'PREMIUM' ? '#92400E' : userSubscription === 'BASE' ? '#374151' : '#2A9FCB'
                                  }}>
                              {userSubscription}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600">
                          Seleziona le prestazioni che desideri. Il costo sarà calcolato in base al tuo abbonamento.
                        </p>

                        {/* Prestazioni per categoria */}
                        {['visite_principali', 'singole_medico', 'ortottista', 'trattamenti', 'valutazioni'].map(category => {
                          const categoryServices = LISTINO_PRESTAZIONI.filter(s => s.category === category);
                          if (categoryServices.length === 0) return null;
                          
                          const categoryLabels = {
                            visite_principali: '👁️ Visite Principali',
                            singole_medico: '🔬 Prestazioni Singole (Medico)',
                            ortottista: '👤 Prestazioni Ortottista',
                            trattamenti: '💊 Trattamenti Terapeutici',
                            valutazioni: '📋 Valutazioni Pre-Chirurgiche'
                          };
                          
                          return (
                            <div key={category} className="mb-6">
                              <h4 className="text-sm font-semibold mb-3 text-gray-700 uppercase tracking-wide border-b pb-2">
                                {categoryLabels[category]}
                              </h4>
                              <div className="space-y-2">
                                {categoryServices.map(service => {
                                  const isInCart = cart.find(item => item.id === service.id);
                                  const isCompatible = isServiceCompatible(service);
                                  
                                  // Calcola se è incluso nell'abbonamento
                                  let isIncluded = false;
                                  let priceLabel = service.price === 0 ? 'Inclusa' : `€${service.price}`;
                                  
                                  if (userSubscription === 'PREMIUM') {
                                    if (service.includedPremium === true) {
                                      isIncluded = true;
                                      priceLabel = 'Incluso';
                                    } else if (typeof service.includedPremium === 'string') {
                                      priceLabel = service.includedPremium;
                                    }
                                  } else if (userSubscription === 'BASE') {
                                    if (service.includedBase === true) {
                                      isIncluded = true;
                                      priceLabel = 'Incluso';
                                    }
                                  }
                                  
                                  // Check if this service is included in a selected visit
                                  const visitaCompleta = cart.find(item => item.id === 'visita_completa');
                                  const controlloGlaucoma = cart.find(item => item.id === 'controllo_glaucoma');
                                  const controlloMaculopatia = cart.find(item => item.id === 'controllo_maculopatia');
                                  
                                  let isIncludedInVisit = false;
                                  if (visitaCompleta && visitaCompleta.includes?.includes(service.id)) {
                                    isIncludedInVisit = true;
                                  }
                                  if (controlloGlaucoma && controlloGlaucoma.includes?.includes(service.id)) {
                                    isIncludedInVisit = true;
                                  }
                                  if (controlloMaculopatia && controlloMaculopatia.includes?.includes(service.id)) {
                                    isIncludedInVisit = true;
                                  }
                                  
                                  return (
                                    <button
                                      key={service.id}
                                      type="button"
                                      onClick={() => isInCart ? removeFromCart(service.id) : addToCart(service)}
                                      disabled={!isCompatible || isIncludedInVisit}
                                      className={`w-full p-3 rounded-lg border-2 transition-all text-left flex items-center justify-between ${
                                        isInCart 
                                          ? 'border-[#2A9FCB] bg-gradient-to-br from-[#e8f4fd] via-[#f4f9fe] to-[#dcedf8]' 
                                          : isIncludedInVisit
                                            ? 'border-green-300 bg-green-50 opacity-75'
                                            : isCompatible 
                                              ? 'border-gray-200 hover:border-[#2A9FCB]/50 bg-white' 
                                              : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                                      }`}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                          isInCart ? 'border-[#2A9FCB] bg-[#2A9FCB]' : isIncludedInVisit ? 'border-green-500 bg-green-500' : 'border-gray-300'
                                        }`}>
                                          {(isInCart || isIncludedInVisit) && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <div>
                                          <p className="font-medium text-gray-800">{service.name}</p>
                                          {service.description && (
                                            <p className="text-xs text-gray-500 mt-0.5">{service.description}</p>
                                          )}
                                          {service.note && service.category !== 'valutazioni' && (
                                            <p className="text-xs text-amber-600 mt-0.5">{service.note}</p>
                                          )}
                                          <p className="text-xs text-gray-400">{service.duration} min</p>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        {isIncludedInVisit ? (
                                          <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                                            ✓ Incluso nella visita
                                          </span>
                                        ) : service.category === 'valutazioni' ? (
                                          <span className="text-xs text-gray-500">
                                            {service.note}
                                          </span>
                                        ) : (
                                          <span className={`font-semibold ${isIncluded ? 'text-green-600' : 'text-gray-800'}`}>
                                            {priceLabel}
                                          </span>
                                        )}
                                        {!isCompatible && (
                                          <p className="text-xs text-red-500 mt-1">Solo ortottista</p>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        {/* Riepilogo Costi */}
                        {cart.length > 0 && (
                          <div className="mt-4 p-4 rounded-xl" style={{background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)', border: '1px solid #99F6E4'}}>
                            <h4 className="font-semibold mb-3" style={{color: '#2A9FCB'}}>
                              Riepilogo Prestazioni ({cart.length})
                            </h4>
                            <div className="space-y-2">
                              {cart.map(item => {
                                let itemPrice = item.price;
                                let isIncluded = false;
                                
                                if (userSubscription === 'PREMIUM' && item.includedPremium === true) {
                                  isIncluded = true;
                                  itemPrice = 0;
                                } else if (userSubscription === 'BASE' && item.includedBase === true) {
                                  isIncluded = true;
                                  itemPrice = 0;
                                }
                                
                                return (
                                  <div key={item.id} className="flex justify-between items-center text-sm">
                                    <span>{item.name}</span>
                                    <span className={isIncluded ? 'text-green-600 font-medium' : ''}>
                                      {isIncluded ? 'Incluso' : `€${itemPrice}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="border-t border-[#99F6E4] mt-3 pt-3">
                              {cartTotal.savings > 0 && (
                                <div className="flex justify-between text-sm text-gray-500 mb-1">
                                  <span>Listino</span>
                                  <span className="line-through">€{cartTotal.original}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-bold text-lg" style={{color: '#2A9FCB'}}>
                                <span>Da pagare al check-in</span>
                                <span>€{cartTotal.discounted}</span>
                              </div>
                              {cartTotal.savings > 0 && (
                                <p className="text-sm text-green-600 mt-1">
                                  Risparmi €{cartTotal.savings} con il tuo abbonamento {userSubscription}!
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {selectedTime && cart.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>
                          Note aggiuntive (opzionale)
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={3}
                          className="w-full p-3 border rounded-lg bg-white resize-none"
                          style={{borderColor: '#E5E7EB'}}
                          placeholder="Sintomi, urgenze, richieste particolari..."
                          data-testid="notes-textarea"
                        />
                      </div>
                    )}

                    {/* Summary before submit */}
                    {selectedDate && selectedTime && selectedDoctor && cart.length > 0 && (
                      <div className="p-4 rounded-xl" style={{background: 'linear-gradient(135deg, #e8f4fd 0%, #CCFBF1 100%)', border: '1px solid #5EEAD4'}}>
                        <h4 className="font-semibold mb-3" style={{color: '#2A9FCB'}}>Riepilogo Prenotazione</h4>
                        <div className="space-y-2 text-sm">
                          {selectedLocation && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Sede</span>
                              <span className={`font-medium ${selectedLocation === 'milano' ? 'text-orange-600' : 'text-[#2A9FCB]'}`}>
                                {selectedLocation === 'milano' ? 'Milano — Via Santa Sofia 10' : 'Napoli — Via Posillipo 160'}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">Data</span>
                            <span className="font-medium">{new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Orario</span>
                            <span className="font-medium">{selectedTime}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Medico</span>
                            <span className="font-medium">Dott. {selectedDoctor.first_name} {selectedDoctor.last_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Prestazioni</span>
                            <span className="font-medium">{cart.length} selezionate</span>
                          </div>
                          <div className="border-t pt-2 mt-2 flex justify-between font-bold" style={{borderColor: '#5EEAD4', color: '#2A9FCB'}}>
                            <span>Totale da pagare</span>
                            <span>€{cartTotal.discounted}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={!selectedDate || !selectedTime || cart.length === 0 || bookingLoading}
                      className="w-full py-4 rounded-xl text-white font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{background: '#2A9FCB'}}
                      data-testid="submit-booking"
                    >
                      {bookingLoading ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Prenotazione in corso...
                        </>
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          Conferma Prenotazione {cartTotal.discounted > 0 ? `(€${cartTotal.discounted})` : ''}
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Booking;
